import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, Signal, computed, effect, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/services/http.service';
import { Zone } from '../../bo/zone';
import { FonctionsService } from '../../services/fonctions-partagees.service';
import { UtilisateurService } from '../../services/utilisateur.service';

@Component({
  selector: 'app-zone',
  templateUrl: './zone.component.html',
  styleUrls: ['./zone.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ZoneComponent implements AfterViewInit {
  readonly dataSource = signal(new MatTableDataSource<Zone>());
  readonly originalZone = signal<Zone | null>(null);

  readonly sort = viewChild.required<MatSort>(MatSort);
  readonly isEditMode = signal(false);
  readonly erreurRecuperation = signal(false);

  readonly displayedColumns: string[] = ['nom', 'isActif', 'burden', 'spacing', 'minBurden', 'diameter', 'inclination', 'subdrill'];

  // Reactive form — source of truth for all input fields
  readonly form: FormGroup;

  // Derived reactive state — no manual .set() calls needed
  readonly isModified: Signal<boolean>;
  readonly nomInvalide: Signal<boolean>;
  readonly isActifValue: Signal<boolean>;

  // Tracks whether the user has attempted a submit (gates nomInvalide display)
  private readonly submitAttempted = signal(false);

  // Signal bridge from form.valueChanges so computed() can track form state
  private formValue: Signal<Partial<{ nom: string; isActif: boolean; burden: number | null; spacing: number | null; minBurden: number | null; diameter: number | null; inclination: number | null; subdrill: number | null }>>;
  private nomStatus: Signal<string | undefined>;

  constructor(
    private readonly http: HttpService,
    private readonly utilService: UtilisateurService,
    private readonly fonctionsService: FonctionsService,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef
  ) {
    this.form = this.fb.group({
      nom:         ['', [Validators.required, Validators.maxLength(256)]],
      isActif:     [true],
      burden:      [null as number | null],
      spacing:     [null as number | null],
      minBurden:   [null as number | null],
      diameter:    [null as number | null],
      inclination: [null as number | null],
      subdrill:    [null as number | null]
    });

    // Bridge observables → signals (must be in injection context, i.e. constructor)
    this.formValue = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });
    this.nomStatus = toSignal(this.form.controls['nom'].statusChanges, { initialValue: this.form.controls['nom'].status });

    this.isModified = computed(() => {
      const val = this.formValue();
      if (!this.isEditMode()) return !!(val?.nom?.trim());
      const original = this.originalZone();
      if (!original) return false;
      return val.nom         !== original.nom         ||
             val.isActif     !== original.isActif     ||
             val.burden      !== original.burden      ||
             val.spacing     !== original.spacing     ||
             val.minBurden   !== original.minBurden   ||
             val.diameter    !== original.diameter    ||
             val.inclination !== original.inclination ||
             val.subdrill    !== original.subdrill;
    });

    this.nomInvalide = computed(() => this.submitAttempted() && this.nomStatus() === 'INVALID');
    this.isActifValue = computed(() => !!(this.formValue() as any)?.isActif);

    // Fixed: reads the signal inside the effect so site changes are tracked
    effect(() => {
      const site = this.utilService.currentSite();
      if (site) {
        // Clear any in-progress edit / selection from the previous site
        this.resetForm();
        this.dataSource().data = [];
        this.refreshData();
      }
    });
  }

  ngAfterViewInit(): void {
    this.dataSource().sort = this.sort();
  }

  refreshData(): void {
    this.erreurRecuperation.set(false);
    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);
    this.http.get<Zone[]>('Sites/' + this.utilService.currentSite()?.siteId + '/Zones')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          clearTimeout(startLoader);
          this.fonctionsService.loading_screen(false);
        })
      )
      .subscribe({
        next: result => {
          this.dataSource().data = result;
        },
        error: () => {
          this.erreurRecuperation.set(true);
        }
      });
  }

  selectZone(zone: Zone): void {
    // Toggle: if clicking the same zone again, deselect it
    if (this.isEditMode() && this.originalZone()?.zoneId === zone.zoneId) {
      this.resetForm();
      return;
    }

    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);
    this.http.get<Zone>('Zones/' + zone.zoneId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          clearTimeout(startLoader);
          this.fonctionsService.loading_screen(false);
        })
      )
      .subscribe({
        next: z => {
          const loaded = Object.assign(new Zone(), z);
          this.originalZone.set(loaded);
          this.form.patchValue({
            nom:         loaded.nom,
            isActif:     loaded.isActif,
            burden:      loaded.burden,
            spacing:     loaded.spacing,
            minBurden:   loaded.minBurden,
            diameter:    loaded.diameter,
            inclination: loaded.inclination,
            subdrill:    loaded.subdrill
          });
          this.form.markAsPristine();
          this.form.markAsUntouched();
          this.submitAttempted.set(false);
          this.isEditMode.set(true);
        },
        error: () => {
          this.translate.get('GLOBAL.ERREUR_RECUP_DONNEES')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(trad => this.fonctionsService.launch_toast(false, false, trad));
        }
      });
  }

  onSubmit(): void {
    this.submitAttempted.set(true);
    if (this.form.invalid) return;

    const val = this.form.getRawValue();
    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);

    const payload = Object.assign(new Zone(), this.originalZone() ?? {}, val, {
      nom:    val.nom.trim(),
      siteId: this.utilService.currentSite()?.siteId
    });

    const request = this.isEditMode()
      ? this.http.put<Zone, Zone>('Zones', payload)
      : this.http.post<Zone, Zone>('Zones', payload);

    request
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          clearTimeout(startLoader);
          this.fonctionsService.loading_screen(false);
        })
      )
      .subscribe({
        next: result => {
          if (result) {
            const zones = [...this.dataSource().data];
            const idx = zones.findIndex(z => z.zoneId === result.zoneId);
            if (idx !== -1) {
              zones[idx] = result;
            } else {
              zones.push(result);
            }
            this.dataSource().data = zones;
            this.fonctionsService.launch_toast(true);
            this.resetForm();
          } else {
            this.fonctionsService.launch_toast(false);
          }
        },
        error: () => {
          this.fonctionsService.launch_toast(false);
        }
      });
  }

  onCancel(): void {
    this.resetForm();
  }

  resetForm(): void {
    this.form.reset({ isActif: true });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.originalZone.set(null);
    this.isEditMode.set(false);
    this.submitAttempted.set(false);
  }
}
